import React, { useEffect, useState } from 'react';
import {
  listerProfils, enregistrerProfil, dupliquerProfil,
  supprimerProfil, definirDefaut, deplacerProfil,
} from '../stockage/profils.js';
import { PROFIL_VIERGE } from '../profils.js';
import Synchronisation from '../composants/Synchronisation.jsx';
import Sauvegarde from '../composants/Sauvegarde.jsx';
import Installation from '../composants/Installation.jsx';
import { doublonsDeNom } from '../profils.js';
import Info from '../composants/Info.jsx';
import { ecrireReglages } from '../stockage/reglages.js';

/**
 * Écran Réglages - document 04, écran 3.
 *
 * Au lot 3, deux sections sur cinq : les profils de corridor et l'apparence.
 * Synchronisation, sauvegarde et « à propos » viennent avec les lots 7 et 8 ;
 * elles sont annoncées plutôt que masquées, pour que l'écart au périmètre reste
 * lisible.
 */
export default function Reglages({ profils, onProfilsChanges, theme, onTheme, reglages, onReglages, onDonneesChangees }) {
  // Le calcul est dans le domaine, pas ici : c'est lui qui sait lequel garder.
  const doublons = doublonsDeNom(profils);

  // Écriture immédiate : un seuil qu'il faudrait penser à valider serait
  // silencieusement perdu au changement d'écran.
  const majReglage = async (cle, valeur) => onReglages(await ecrireReglages({ [cle]: valeur }));
  const [edition, setEdition] = useState(null);

  const rafraichir = async (promesse) => {
    await promesse;
    onProfilsChanges(await listerProfils());
  };

  return (
    <div style={{ display: 'grid', gap: 'var(--espace-5)' }}>
      <section>
        <h2 style={{ fontFamily: 'var(--police-titre)', fontSize: '1.1rem', margin: '0 0 var(--espace-3)' }}>
          Profils de corridor
        </h2>

        {/* Signalé et non corrigé d'office : supprimer des profils sans le
            demander serait pire que le désordre qu'on répare. */}
        {doublons.length > 0 && (
          <div
            role="status"
            style={{
              marginBottom: 'var(--espace-3)', padding: 'var(--espace-3)',
              border: '1px solid var(--couleur-trait-fort)', borderRadius: 'var(--rayon)',
              background: 'var(--couleur-surface)', fontSize: '0.85rem',
            }}
          >
            <p style={{ margin: '0 0 var(--espace-2)' }}>
              {doublons.length === 1
                ? <>Deux profils portent le nom <strong>{doublons[0].nom}</strong>.</>
                : <>{doublons.length} noms de profil sont portés par plusieurs entrées.</>}
              {' '}Le sélecteur de l’écran Coter les affiche à l’identique, ce qui
              donne l’impression qu’en changer ne fait rien.
            </p>
            <button
              type="button"
              onClick={() => {
                const noms = doublons.flatMap((g) => g.aSupprimer.map((p) => `${p.name} (aller ${p.rateOut})`));
                if (!window.confirm(`Supprimer ${noms.length} profil(s) en double ?\n\n${noms.join('\n')}\n\nLe profil le plus complet de chaque nom est conservé.`)) return;
                rafraichir((async () => {
                  let liste = profils;
                  for (const g of doublons) {
                    for (const p of g.aSupprimer) liste = await supprimerProfil(p.id);
                  }
                  return liste;
                })());
              }}
              style={{
                padding: 'var(--espace-2) var(--espace-4)',
                border: '1px solid var(--couleur-accent)', borderRadius: 'var(--rayon)',
                background: 'var(--couleur-accent)', color: 'var(--couleur-sur-accent)',
                fontWeight: 600, fontSize: '0.85rem',
              }}
            >
              Supprimer les doublons
            </button>
          </div>
        )}

        <div style={{ display: 'grid', gap: 'var(--espace-2)' }}>
          {profils.map((p, i) => (
            <div key={p.id} className="carte" style={{ padding: 'var(--espace-3)' }}>
              <div style={{ display: 'grid', gap: 'var(--espace-3)' }}>
                <div>
                  <strong>{p.name || 'Sans nom'}</strong>
                  {p.isDefault && (
                    <span style={{
                      marginLeft: 'var(--espace-2)', padding: '1px 6px',
                      background: 'var(--couleur-accent)', color: 'var(--couleur-sur-accent)',
                      borderRadius: 'var(--rayon)', fontSize: '0.8rem',
                    }}>défaut</span>
                  )}
                  <div className="chiffre" style={{ fontSize: '0.8rem', color: 'var(--couleur-encre-douce)' }}>
                    {p.cfaCurrency} · aller {p.rateOut} · retour {p.rateIn} · {p.servicePercent} %
                  </div>
                </div>
                {/* Les actions passent sous le nom plutôt qu'à sa droite. Sur
                    la même ligne, elles se répartissaient sur trois rangées
                    bancales dès qu'on descendait sous 420 px, avec un bouton
                    orphelin à chaque fois. */}
                <div className="actions">
                  <Action onClick={() => setEdition(p)}>Éditer</Action>
                  <Action onClick={() => rafraichir(dupliquerProfil(p))}>Dupliquer</Action>
                  {!p.isDefault && <Action onClick={() => rafraichir(definirDefaut(p.id))}>Par défaut</Action>}
                  <Action
                    onClick={() => {
                      // Le dernier profil n'est pas supprimable : l'écran Coter
                      // n'aurait plus rien à sélectionner.
                      if (profils.length === 1) return;
                      if (window.confirm(`Supprimer « ${p.name} » ?`)) rafraichir(supprimerProfil(p.id));
                    }}
                    desactive={profils.length === 1}
                    titre={profils.length === 1 ? 'Le dernier profil ne peut pas être supprimé' : 'Supprimer'}
                  >
                    Supprimer
                  </Action>
                  {/* Les flèches partagent une cellule : deux boutons d'une
                      seule glyphe méritent moins de place que « Dupliquer ». */}
                  <span className="actions" style={{ display: 'flex' }}>
                    <Action onClick={() => rafraichir(deplacerProfil(p.id, -1))} desactive={i === 0} titre="Monter">↑</Action>
                    <Action onClick={() => rafraichir(deplacerProfil(p.id, 1))} desactive={i === profils.length - 1} titre="Descendre">↓</Action>
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>

        <button
          type="button"
          onClick={() => setEdition({ ...PROFIL_VIERGE, order: profils.length })}
          style={{
            marginTop: 'var(--espace-3)', padding: 'var(--espace-2) var(--espace-4)',
            border: '1px solid var(--couleur-accent)', borderRadius: 'var(--rayon)',
            background: 'var(--couleur-accent)', color: 'var(--couleur-sur-accent)', fontWeight: 600,
          }}
        >
          Nouveau profil
        </button>
      </section>

      <Synchronisation
        reglages={reglages}
        onReglages={onReglages}
        onDonneesChangees={onDonneesChangees}
      />

      <Sauvegarde onDonneesChangees={onDonneesChangees} />

      <Installation />

      <section>
        <h2 style={{ fontFamily: 'var(--police-titre)', fontSize: '1.1rem', margin: '0 0 var(--espace-3)' }}>
          Seuils d’alerte
        </h2>
        <p style={{ margin: '0 0 var(--espace-3)', fontSize: '0.85rem', color: 'var(--couleur-encre-douce)' }}>
          Ce que l’écran Position doit signaler. Ces alertes ne bloquent jamais
          une opération : une caisse à découvert est comptablement licite, c’est
          un outil de suivi et non un contrôle bancaire.
        </p>
        <div className="grille-2">
          {[
            ['seuilCaisseEur', 'Caisse EUR sous', 'EUR'],
            ['seuilCaisseXaf', 'Caisse XAF sous', 'XAF'],
            ['seuilCaisseXof', 'Caisse XOF sous', 'XOF'],
          ].map(([cle, libelle, unite]) => (
            <label key={cle} style={{ display: 'block' }}>
              <span className="etiquette">{libelle} ({unite})</span>
              <input
                type="number"
                value={reglages[cle] ?? 0}
                onChange={(e) => majReglage(cle, Number(e.target.value))}
                className="champ"
                style={{ marginTop: 'var(--espace-1)' }}
              />
            </label>
          ))}
          <label style={{ display: 'block' }}>
            <span className="etiquette">
              Exposition au-delà de (EUR)
              <Info titre="Exposition">
                Le volume d’euros porté sans être revendu. Il mesure ce que vous
                risquez si le taux de revente se dégrade : tant qu’ils sont en
                stock, leur valeur n’est pas acquise. Laissez vide pour ne
                jamais être alerté.
              </Info>
            </span>
            <input
              type="number"
              value={reglages.seuilExpoEur ?? ''}
              placeholder="aucune alerte"
              onChange={(e) => majReglage('seuilExpoEur', e.target.value === '' ? null : Number(e.target.value))}
              className="champ"
              style={{ marginTop: 'var(--espace-1)' }}
            />
          </label>
        </div>
      </section>

      <section>
        <h2 style={{ fontFamily: 'var(--police-titre)', fontSize: '1.1rem', margin: '0 0 var(--espace-3)' }}>
          Apparence
        </h2>
        <label style={{ display: 'block', maxWidth: 280 }}>
          <span className="etiquette">Direction visuelle</span>
          <select
            value={theme}
            onChange={(e) => onTheme(e.target.value)}
            className="champ"
            style={{ marginTop: 'var(--espace-1)', fontFamily: 'var(--police-texte)' }}
          >
            <option value="corridor">Corridor</option>
            <option value="acloud">aCloud</option>
          </select>
        </label>
      </section>

      <section>
        <h2 style={{ fontFamily: 'var(--police-titre)', fontSize: '1.1rem', margin: '0 0 var(--espace-2)' }}>
          À propos
        </h2>
        <p style={{ margin: 0, color: 'var(--couleur-encre-douce)', fontSize: '0.875rem' }}>
          Corridor FX {import.meta.env.VITE_APP_VERSION ?? 'dev'} - outil de cotation et de
          suivi. <strong>Ce n’est pas un service de paiement</strong> : aucun fonds n’y transite,
          aucune opération n’y est exécutée.
        </p>
      </section>

      {edition && (
        <FormulaireProfil
          profil={edition}
          onAnnuler={() => setEdition(null)}
          onValider={async (p) => { await rafraichir(enregistrerProfil(p)); setEdition(null); }}
        />
      )}
    </div>
  );
}

function Action({ children, onClick, desactive = false, titre }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={desactive}
      title={titre}
      style={{
        padding: 'var(--espace-1) var(--espace-3)', border: '1px solid var(--couleur-trait-fort)',
        borderRadius: 'var(--rayon)', background: 'var(--couleur-fond)',
        color: 'var(--couleur-encre)', fontSize: '0.8rem', cursor: desactive ? 'not-allowed' : 'pointer',
      }}
    >
      {children}
    </button>
  );
}

function FormulaireProfil({ profil, onAnnuler, onValider }) {
  const [p, setP] = useState(profil);
  const maj = (partiel) => setP((v) => ({ ...v, ...partiel }));

  const nombre = (cle, etiquette, pas = '0.01') => (
    <label style={{ display: 'block' }}>
      <span className="etiquette">{etiquette}</span>
      <input
        type="number" step={pas} value={p[cle]}
        onChange={(e) => maj({ [cle]: Number(e.target.value) })}
        className="champ" style={{ marginTop: 'var(--espace-1)' }}
      />
    </label>
  );

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={p.id ? 'Éditer le profil' : 'Nouveau profil'}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)',
        display: 'grid', placeItems: 'center', padding: 'var(--espace-3)', zIndex: 50,
      }}
      onClick={(e) => e.target === e.currentTarget && onAnnuler()}
    >
      <form
        className="carte"
        onSubmit={(e) => { e.preventDefault(); onValider(p); }}
        style={{ maxWidth: 560, width: '100%', maxHeight: '90vh', overflowY: 'auto' }}
      >
        <h3 style={{ marginTop: 0, fontFamily: 'var(--police-titre)' }}>
          {p.id ? 'Éditer le profil' : 'Nouveau profil'}
        </h3>

        <div style={{ display: 'grid', gap: 'var(--espace-3)' }}>
          <label style={{ display: 'block' }}>
            <span className="etiquette">Nom</span>
            <input
              type="text" required value={p.name}
              onChange={(e) => maj({ name: e.target.value })}
              placeholder="XAF · MTN MoMo"
              className="champ"
              style={{ marginTop: 'var(--espace-1)', fontFamily: 'var(--police-texte)' }}
            />
          </label>

          <div className="grille-2">
            <label style={{ display: 'block' }}>
              <span className="etiquette">Devise CFA</span>
              <select
                value={p.cfaCurrency}
                onChange={(e) => maj({ cfaCurrency: e.target.value })}
                className="champ" style={{ marginTop: 'var(--espace-1)' }}
              >
                <option value="XAF">XAF</option>
                <option value="XOF">XOF</option>
              </select>
            </label>
            {nombre('officialRate', 'Parité officielle', '0.001')}
          </div>

          <div className="grille-2">
            {nombre('rateOut', 'Taux aller (EUR → CFA)')}
            {nombre('rateIn', 'Taux retour (CFA → EUR)')}
          </div>

          <div className="grille-2">
            {nombre('serviceFixed', 'Frais fixe (EUR)')}
            {nombre('servicePercent', 'Commission (%)', '0.1')}
          </div>

          <div className="grille-3">
            {nombre('operatorPercent', 'Opérateur (%)')}
            {nombre('operatorFixed', 'Opérateur fixe', '1')}
            {nombre('operatorCap', 'Plafond / transfert', '1000')}
          </div>

          <label style={{ display: 'block' }}>
            <span className="etiquette">Mode de frais par défaut</span>
            <select
              value={p.feeMode}
              onChange={(e) => maj({ feeMode: e.target.value })}
              className="champ" style={{ marginTop: 'var(--espace-1)' }}
            >
              <option value="refacture">Refacturés</option>
              <option value="deduit">Déduits</option>
              <option value="acharge">À ma charge</option>
            </select>
          </label>

          <label style={{ display: 'flex', gap: 'var(--espace-2)', alignItems: 'center' }}>
            <input
              type="checkbox" checked={p.isDefault}
              onChange={(e) => maj({ isDefault: e.target.checked })}
            />
            <span style={{ fontSize: '0.9rem' }}>Profil proposé par défaut</span>
          </label>
        </div>

        <div style={{ display: 'flex', gap: 'var(--espace-2)', marginTop: 'var(--espace-4)' }}>
          <button
            type="submit"
            style={{
              padding: 'var(--espace-2) var(--espace-4)', border: '1px solid var(--couleur-accent)',
              borderRadius: 'var(--rayon)', background: 'var(--couleur-accent)',
              color: 'var(--couleur-sur-accent)', fontWeight: 600,
            }}
          >
            Enregistrer
          </button>
          <button
            type="button" onClick={onAnnuler}
            style={{
              padding: 'var(--espace-2) var(--espace-4)', border: '1px solid var(--couleur-trait-fort)',
              borderRadius: 'var(--rayon)', background: 'transparent', color: 'var(--couleur-encre)',
            }}
          >
            Annuler
          </button>
        </div>
      </form>
    </div>
  );
}
